/**
 * faq-tracking.js — FAQ 事前解決率の計測 (GA4)
 *
 * 目的: 「問い合わせの事前解決率」を定量化するための一次データを取る。
 *       FAQ は 2 面あり、両方を同一イベント体系で計測する。
 *         (1) FAQ ページ  : テーマ内製セクション sections/faq.liquid (.faq__content details)
 *         (2) ウィジェット: Chatty (Avada FAQs) — 本体 DOM に描画されるため
 *                           アプリのプラン/分析機能に依存せず自前で計測できる
 *                           (iframe でも Shadow DOM でもないことを実機で確認済み)
 *
 * 送信イベント (GA4):
 *   faq_open          どの質問が開かれたか   params: faq_source / faq_question / faq_category
 *   faq_search        ウィジェット内の検索語  params: faq_source / faq_query
 *   faq_contact_click 解決せず連絡に回った   params: faq_source / faq_channel (phone|email)
 *   faq_widget_open   ウィジェット起動        params: faq_source
 *   faq_order_track   注文追跡の利用          params: faq_source
 *
 * 事前解決率は「FAQ に触れたセッション」に対する faq_contact_click / お問い合わせ到達の
 * 裏返しで算出する。閲覧数そのものは解決率ではない (探して見つからない時も増えるため)。
 *
 * 配線: layout/theme.liquid の </body> 直前。ウィジェットは全ページ表示 (displayPages: all)
 *       のため、FAQ ページ限定にはしない。
 *
 * 実装方針: 既存の trackSimulator / trackHelp パターン踏襲。
 *           gtag + dataLayer 両系へ送信、未定義環境は no-op、例外は握りつぶす。
 */
(function () {
  'use strict';

  var WIDGET_ROOT = '.Avada-Faqs_Popup';
  var MAX_PARAM_LEN = 100; // GA4 のイベントパラメータ値の上限

  // --------------------------------------------------------------
  // 送信共通
  // --------------------------------------------------------------
  function clean(value) {
    if (!value) return '';
    return String(value).replace(/\s+/g, ' ').trim().slice(0, MAX_PARAM_LEN);
  }

  function track(eventName, params) {
    try {
      var payload = Object.assign({ tool: 'faq' }, params || {});
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      }
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: eventName }, payload));
      }
    } catch (e) { /* swallow */ }
  }

  /**
   * 検索語を GA4 に送る前のサニタイズ。
   * 検索欄は自由入力なのでメールアドレス・電話番号・注文番号が打ち込まれうる。
   * GA4 に個人情報を送らないため、該当する語は丸ごと捨てる (伏字化ではなく破棄)。
   */
  function sanitizeQuery(raw) {
    var q = clean(raw);
    if (!q) return '';
    if (q.indexOf('@') !== -1) return '';        // メールアドレス
    if (/\d{6,}/.test(q.replace(/[-\s]/g, ''))) return ''; // 電話番号 / 注文番号など長い数字列
    return q;
  }

  // --------------------------------------------------------------
  // (1) FAQ ページ側: .faq__content 内の details 開封
  //     - toggle はバブルしないので capture で拾う
  //     - 閉じた時は送らない (open 時のみ)
  //     - 他ページのアコーディオンを拾わないよう .faq__content に限定
  // --------------------------------------------------------------
  function categoryOf(node) {
    // .faq__content の直下は [カテゴリ見出し, 質問, 質問, ...] の並び。
    // 質問要素から前方向の兄弟を辿って直近の見出しを拾う。
    try {
      var el = node;
      while (el && el.parentElement && !el.parentElement.classList.contains('faq__content')) {
        el = el.parentElement;
      }
      if (!el) return '';
      var prev = el.previousElementSibling;
      while (prev) {
        if (prev.classList && prev.classList.contains('faq__category')) {
          return clean(prev.textContent);
        }
        prev = prev.previousElementSibling;
      }
    } catch (e) { /* swallow */ }
    return '';
  }

  document.addEventListener('toggle', function (event) {
    try {
      var details = event.target;
      if (!details || details.tagName !== 'DETAILS' || !details.open) return;
      if (!details.closest('.faq__content')) return;

      var summary = details.querySelector('summary');
      var question = summary ? clean(summary.textContent) : '';
      if (!question) return;

      track('faq_open', {
        faq_source: 'page',
        faq_question: question,
        faq_category: categoryOf(details)
      });
    } catch (e) { /* swallow */ }
  }, true);

  // --------------------------------------------------------------
  // (2) ウィジェット側 (Chatty / Avada FAQs)
  //     本体 DOM に後から描画されるため、document 委譲で拾う。
  // --------------------------------------------------------------
  document.addEventListener('click', function (event) {
    try {
      var target = event.target;
      if (!target || !target.closest) return;

      // 起動ボタン
      if (target.closest('#Avada-FAQ_WidgetTrigger')) {
        track('faq_widget_open', { faq_source: 'widget' });
        return;
      }

      if (!target.closest(WIDGET_ROOT)) return;

      // 質問クリック
      var item = target.closest('.Avada-List-Item');
      if (item) {
        var question = clean(item.textContent);
        if (question) {
          track('faq_open', {
            faq_source: 'widget',
            faq_question: question,
            faq_category: ''  // ウィジェット側は DOM 上にカテゴリ名を持たない
          });
        }
        return;
      }

      // 連絡導線 (= 事前解決に失敗したシグナル)
      var link = target.closest('a[href]');
      if (link) {
        var href = link.getAttribute('href') || '';
        if (href.indexOf('tel:') === 0) {
          track('faq_contact_click', { faq_source: 'widget', faq_channel: 'phone' });
          return;
        }
        if (href.indexOf('mailto:') === 0) {
          track('faq_contact_click', { faq_source: 'widget', faq_channel: 'email' });
          return;
        }
      }

      // 注文追跡カード
      // 注記: このカードだけ固有 class を持たないため文言判定。
      //       Chatty 側の翻訳文言を変えるとここは黙って外れる。
      var card = target.closest('[class*="Avada"]');
      if (card && card.textContent && card.textContent.indexOf('注文を追跡') !== -1) {
        track('faq_order_track', { faq_source: 'widget' });
      }
    } catch (e) { /* swallow */ }
  }, true);

  // ウィジェット内検索 —「FAQ に無い質問」が生で取れる最重要データ
  // 入力中に毎回送らないよう、打ち終わり (900ms 無操作) で 1 回だけ送る。
  var searchTimer = null;
  var lastSentQuery = '';

  document.addEventListener('input', function (event) {
    try {
      var input = event.target;
      if (!input || input.tagName !== 'INPUT') return;
      if (!input.closest || !input.closest(WIDGET_ROOT)) return;

      var value = input.value;
      if (searchTimer) clearTimeout(searchTimer);
      searchTimer = setTimeout(function () {
        var query = sanitizeQuery(value);
        if (!query || query.length < 2) return;
        if (query === lastSentQuery) return;  // 同一語の連投を抑止
        lastSentQuery = query;
        track('faq_search', { faq_source: 'widget', faq_query: query });
      }, 900);
    } catch (e) { /* swallow */ }
  }, true);
})();
